/* eslint-disable jsx-a11y/control-has-associated-label */
import { DataTableContent, DataTableBody } from "@rmwc/data-table"
import "@rmwc/data-table/styles"
import "@rmwc/icon/styles"
import { useTable, useSortBy, useGroupBy, TableOptions } from "react-table"
import { Box } from "../Box"
import { CenteredLoader } from "../Loader"
import { CSSFn } from "../styles"
import {
  DataTable,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  DataTableFooterRow,
  DataTableFooterCell,
} from "./styles"
import * as styles from "./tableStyles"
import { TableHeadCellRenderer } from "./components/TableHeadCellRenderer"
import { TableHeadCell } from "./styles/DataTableHeadCell"
import { Row } from "./types"
import { NoDataMessage } from "./components/NoDataMessage"
import { Link } from "../Link"

export type TableDataTestIds = {
  root?: string
  row?: string
}

export type TableProps<T extends Record<string, unknown>> = TableOptions<T> & {
  css?: { table?: CSSFn; cell?: CSSFn; header?: CSSFn; row?: CSSFn }
  dataTestIds?: TableDataTestIds
  rowOnClick?: (value: unknown, e?: React.MouseEvent<HTMLElement>) => void
  rowOnClickValueKey?: keyof T
  loading?: boolean
  showFooter?: boolean
  renderRowAsAnchor?: boolean
  buildUrl?: (row: Row<T>) => string
  selectedRows?: ReadonlyArray<T>
}

export const Table = <T extends Record<string, unknown>>({
  columns,
  data,
  css,
  dataTestIds,
  rowOnClick,
  rowOnClickValueKey,
  loading,
  showFooter,
  renderRowAsAnchor,
  buildUrl,
  selectedRows,
}: TableProps<T>) => {
  const { getTableProps, getTableBodyProps, prepareRow, headerGroups, rows, footerGroups } = useTable(
    { columns, data },
    useGroupBy,
    useSortBy
  )
  return (
    <Box css={styles.contentContainer}>
      {loading ? (
        <CenteredLoader />
      ) : (
        <DataTable data-testid={dataTestIds?.root} css={css?.table} {...getTableProps}>
          <DataTableContent>
            <DataTableHead>
              {headerGroups.map((headerGroup) => (
                <DataTableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableHeadCell {...column.getHeaderProps()} css={css?.header}>
                      <TableHeadCellRenderer column={column} />
                    </TableHeadCell>
                  ))}
                </DataTableRow>
              ))}
            </DataTableHead>
            {data.length ? (
              <DataTableBody {...getTableBodyProps()}>
                {rows.map((row: Row<T>) => {
                  prepareRow(row)
                  return (
                    <DataTableRow
                      action={rowOnClick}
                      actionValue={rowOnClickValueKey ? row.original[rowOnClickValueKey] : undefined}
                      {...row.getRowProps()}
                      css={selectedRows?.some((selected) => selected.id === row.original.id) ? css?.row : undefined}
                      data-testid={dataTestIds?.row}
                    >
                      {row.cells.map((cell) => (
                        <DataTableCell
                          css={renderRowAsAnchor === true ? styles.removePaddings : css?.cell}
                          {...cell.getCellProps()}
                        >
                          {renderRowAsAnchor ? (
                            <Link css={styles.customLink} href={buildUrl ? buildUrl(row) : `#`}>
                              <Box css={styles.cellsStyles}>{cell.render(`Cell`)}</Box>
                            </Link>
                          ) : (
                            cell.render(`Cell`)
                          )}
                        </DataTableCell>
                      ))}
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            ) : (
              <DataTableBody>
                <DataTableRow>
                  <td colSpan={headerGroups[0].headers.length}>
                    <NoDataMessage />
                  </td>
                </DataTableRow>
              </DataTableBody>
            )}
            {showFooter && (
              <tfoot>
                {footerGroups.map((group) => (
                  <DataTableFooterRow {...group.getFooterGroupProps()}>
                    {group.headers.map((column) => (
                      <DataTableFooterCell {...column.getFooterProps()}>{column.render(`Footer`)}</DataTableFooterCell>
                    ))}
                  </DataTableFooterRow>
                ))}
              </tfoot>
            )}
          </DataTableContent>
        </DataTable>
      )}
    </Box>
  )
}

Table.defaultProps = {
  css: undefined,
  dataTestIds: undefined,
  rowOnClick: undefined,
  rowOnClickValueKey: undefined,
  buildUrl: undefined,
  loading: false,
  showFooter: false,
  renderRowAsAnchor: false,
  selectedRows: undefined,
}
